import { api, getActiveAuthScope } from './client';
import type {
  AdminRole,
  AdminVerificationUserRow,
  AuthSession,
  MyProfile,
  PublicUser,
  UserRole,
  VerifyOtpResponse,
} from '../types/api';

export async function requestOtp(
  email: string,
  intendedRole?: UserRole,
  isSignup?: boolean,
): Promise<void> {
  await api.post('/auth/otp/request', { email, intendedRole, isSignup });
}

export async function verifyOtp(
  email: string,
  code: string,
  intendedRole?: UserRole,
): Promise<VerifyOtpResponse> {
  const { data } = await api.post<VerifyOtpResponse>('/auth/otp/verify', {
    email,
    code,
    intendedRole,
  });
  return data;
}

// Admin-console endpoints. Intentionally a separate path from the public OTP
// flow above so we can rate-limit them tighter on the server and only ever
// mint admin-audience JWTs here.
export async function requestAdminOtp(email: string): Promise<void> {
  await api.post('/auth/admin/otp/request', { email });
}

export async function verifyAdminOtp(email: string, code: string): Promise<AuthSession> {
  const { data } = await api.post<AuthSession>('/auth/admin/otp/verify', { email, code });
  return data;
}

export async function requestAdminRegisterOtp(email: string): Promise<void> {
  await api.post('/auth/admin/register/otp/request', { email });
}

export async function verifyAdminRegisterOtp(
  email: string,
  otpCode: string,
): Promise<{ success: boolean; signupToken: string }> {
  const { data } = await api.post<{ success: boolean; signupToken: string }>(
    '/auth/admin/register/otp/verify',
    { email, otpCode },
  );
  return data;
}

export interface AdminRegisterPayload {
  firstName: string;
  lastName: string;
  mobile: string;
  email: string;
  otpCode?: string;
  signupToken?: string;
  password: string;
  adminRole: AdminRole;
}

export async function adminRegister(payload: AdminRegisterPayload): Promise<AuthSession> {
  const { data } = await api.post<AuthSession>('/auth/admin/register', payload);
  return data;
}

export interface AdminSigninPayload {
  email: string;
  password: string;
}

export async function adminSignin(payload: AdminSigninPayload): Promise<AuthSession> {
  const { data } = await api.post<AuthSession>('/auth/admin/signin', payload);
  return data;
}

export interface AdminResubmitProfilePayload {
  firstName: string;
  lastName: string;
  mobile: string;
  adminRole: AdminRole;
}

export async function adminResubmitProfile(
  payload: AdminResubmitProfilePayload,
): Promise<PublicUser> {
  const { data } = await api.patch<PublicUser>('/auth/admin/me/resubmit', payload);
  return data;
}

export async function fetchAdminVerificationUsers(
  status?: string,
): Promise<AdminVerificationUserRow[]> {
  const { data } = await api.get<AdminVerificationUserRow[]>('/admin/verification/users', {
    params: status ? { status } : undefined,
  });
  return data;
}

export async function acceptAdminVerificationUser(id: string): Promise<AdminVerificationUserRow> {
  const { data } = await api.post<AdminVerificationUserRow>(
    `/admin/verification/users/${id}/accept`,
  );
  return data;
}

export async function rejectAdminVerificationUser(
  id: string,
  reason: string,
): Promise<AdminVerificationUserRow> {
  const { data } = await api.post<AdminVerificationUserRow>(
    `/admin/verification/users/${id}/reject`,
    { reason },
  );
  return data;
}

export interface ClientSignupPayload {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  mobile?: string;
  otpCode: string;
}

export async function clientSignup(payload: ClientSignupPayload): Promise<AuthSession> {
  const { data } = await api.post<AuthSession>('/auth/client/signup', payload);
  return data;
}

export interface ClientSigninPayload {
  email: string;
  password: string;
}

export async function clientSignin(payload: ClientSigninPayload): Promise<AuthSession> {
  const { data } = await api.post<AuthSession>('/auth/client/signin', payload);
  return data;
}

export interface CompleteClientSignupPayload {
  signupToken: string;
  firstName: string;
  lastName: string;
  mobile?: string;
}

export async function completeClientSignup(
  payload: CompleteClientSignupPayload,
): Promise<AuthSession> {
  const { data } = await api.post<AuthSession>('/auth/signup/complete', {
    ...payload,
    role: 'CLIENT',
  });
  return data;
}

export type AssociateSignupRole = 'ASSOCIATE' | 'FRANCHISE';

export interface CompleteAssociateSignupPayload {
  signupToken: string;
  role: AssociateSignupRole;
  firstName: string;
  lastName: string;
  mobile: string;
  password?: string;
  profession:
    | 'LAWYER'
    | 'CHARTERED_ACCOUNTANT'
    | 'COMPANY_SECRETARY'
    | 'COST_MANAGEMENT_ACCOUNTANT'
    | 'DOCUMENT_WRITER'
    | 'TAX_CONSULTANT'
    | 'OTHER_DEGREE_HOLDER';
  registrationNo?: string;
  outletName?: string;
  address: {
    pincode: string;
    state: string;
    district: string;
    taluk?: string;
    village?: string;
    landmark?: string;
    street?: string;
  };
}

export async function completeAssociateSignup(
  payload: CompleteAssociateSignupPayload,
): Promise<AuthSession> {
  const { data } = await api.post<AuthSession>('/auth/signup/complete', payload);
  return data;
}

export interface AssociateSigninPayload {
  email: string;
  password: string;
}

export async function associateSignin(payload: AssociateSigninPayload): Promise<AuthSession> {
  const { data } = await api.post<AuthSession>('/auth/associate/signin', payload);
  return data;
}

export async function logout(refreshToken?: string): Promise<void> {
  try {
    const scope = getActiveAuthScope();
    await api.post(`/auth/${scope}/logout`, refreshToken ? { refreshToken } : {});
  } catch {
    // Ignore network error on logout so client-side cleanup proceeds
  }
}

export async function me(): Promise<PublicUser> {
  const { data } = await api.get<PublicUser>('/auth/me');
  return data;
}

export async function fetchMyProfile(): Promise<MyProfile> {
  const { data } = await api.get<MyProfile>('/users/me');
  return data;
}

export interface UpdateProfilePayload {
  firstName?: string;
  lastName?: string;
  mobile?: string;
}

export async function updateProfile(payload: UpdateProfilePayload): Promise<MyProfile> {
  const { data } = await api.patch<MyProfile>('/users/me', payload);
  return data;
}

export interface PasswordResetConfirmPayload {
  email: string;
  otpCode: string;
  newPassword: string;
}

export async function requestPasswordResetOtp(email: string): Promise<{ status: 'sent' }> {
  const { data } = await api.post<{ status: 'sent' }>('/auth/password/reset/request', {
    email: email.trim().toLowerCase(),
  });
  return data;
}

export async function confirmPasswordReset(
  payload: PasswordResetConfirmPayload,
): Promise<{ success: boolean; message: string }> {
  const { data } = await api.post<{ success: boolean; message: string }>(
    '/auth/password/reset/confirm',
    {
      ...payload,
      email: payload.email.trim().toLowerCase(),
    },
  );
  return data;
}
